import { db } from './auth-context';
import {
    doc,
    getDoc,
    getDocs,
    setDoc,
    updateDoc,
    deleteDoc,
    addDoc,
    collection,
    arrayUnion,
    serverTimestamp
} from 'firebase/firestore';

export const defaultDomainMastery = {
    'Security Architecture': 0,
    'General Security Concepts': 0,
    'Security Operations': 0,
    'Threats, Vulnerabilities and Mitigations': 0,
    'Security Program Management': 0
};

// Initialize user document with default values
export const initializeUserData = async (userId) => {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
        const defaultData = {
            totalQuestions: 0,
            correctAnswers: 0,
            totalTime: 0,
            lastSession: null,
            domainMastery: defaultDomainMastery,
            recentSessions: [],
            weakestTopics: [],
            createdAt: serverTimestamp(),
            lastUpdated: serverTimestamp()
        };

        await setDoc(userRef, defaultData);
        return defaultData;
    }

    return userDoc.data();
};

// Fetch user stats with proper domain mastery validation
export const fetchUserStats = async (userId) => {
    try {
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);

        if (!userDoc.exists()) {
            return await initializeUserData(userId);
        }

        const data = userDoc.data();

        // Create a clean domain mastery object using only valid domains
        const cleanDomainMastery = Object.keys(defaultDomainMastery).reduce((acc, domain) => {
            acc[domain] = (data.domainMastery && typeof data.domainMastery[domain] === 'number')
                ? data.domainMastery[domain]
                : defaultDomainMastery[domain];
            return acc;
        }, {});

        return {
            ...data,
            domainMastery: cleanDomainMastery,
            lastPracticedByDomain: data.lastPracticedByDomain || {}
        };
    } catch (error) {
        console.error('Error fetching user stats:', error);
        return {
            totalQuestions: 0,
            correctAnswers: 0,
            totalTime: 0,
            lastSession: null,
            domainMastery: defaultDomainMastery,
            lastPracticedByDomain: {},
            recentSessions: [],
            weakestTopics: []
        };
    }
};

// Update user stats after a practice session
export const updateUserStats = async (userId, sessionData) => {
    try {
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        const currentData = userDoc.exists() ? userDoc.data() : { totalQuestions: 0, correctAnswers: 0, totalTime: 0 };

        const {
            correctAnswers,
            totalQuestions,
            timeSpent,
            domainMastery,
            mode,
            score
        } = sessionData;

        // Validate domain mastery before update
        const validatedDomainMastery = Object.keys(defaultDomainMastery).reduce((acc, domain) => {
            acc[domain] = (domainMastery && typeof domainMastery[domain] === 'number')
                ? domainMastery[domain]
                : defaultDomainMastery[domain];
            return acc;
        }, {});

        const session = {
            mode,
            questionsAnswered: totalQuestions,
            score,
            duration: timeSpent,
            date: new Date().toISOString()
        };

        await updateDoc(userRef, {
            totalQuestions: currentData.totalQuestions + totalQuestions,
            correctAnswers: currentData.correctAnswers + correctAnswers,
            totalTime: (currentData.totalTime || 0) + timeSpent,
            lastSession: session,
            domainMastery: validatedDomainMastery,
            'recentSessions': arrayUnion(session),
            lastUpdated: serverTimestamp()
        });

        console.log('Stats updated successfully');
    } catch (error) {
        console.error('Error updating user stats:', error);
        throw error;
    }
};

// Log an individual response for analytics and spaced repetition
export const logResponse = async (userId, responseData) => {
    try {
        const responsesRef = collection(db, 'users', userId, 'responseLogs');
        await addDoc(responsesRef, {
            ...responseData,
            timestamp: serverTimestamp()
        });
    } catch (error) {
        console.error('Error logging response:', error);
        // Non-blocking: don't throw, response logging is best-effort
    }
};

// Update ability estimate (EAP)
export const updateAbilityEstimate = async (userId, abilityEstimate) => {
    try {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
            abilityEstimate,
            lastUpdated: serverTimestamp()
        });
    } catch (error) {
        console.error('Error updating ability estimate:', error);
    }
};

// Update domain mastery with validation (includes lastPracticedByDomain for decay)
export const updateDomainMastery = async (userId, domainMastery, lastPracticedByDomain = null) => {
    try {
        const userRef = doc(db, 'users', userId);

        // Validate domain mastery before update
        const validatedDomainMastery = Object.keys(defaultDomainMastery).reduce((acc, domain) => {
            acc[domain] = (domainMastery && typeof domainMastery[domain] === 'number')
                ? domainMastery[domain]
                : defaultDomainMastery[domain];
            return acc;
        }, {});

        const updateData = {
            domainMastery: validatedDomainMastery,
            lastUpdated: serverTimestamp()
        };

        // Persist lastPracticedByDomain timestamps for mastery decay
        if (lastPracticedByDomain) {
            updateData.lastPracticedByDomain = lastPracticedByDomain;
        }

        await updateDoc(userRef, updateData);
    } catch (error) {
        console.error('Error updating domain mastery:', error);
    }
};

// ============================================================
// Item History (Spaced Repetition) — Firestore subcollection
// Path: users/{uid}/itemHistory/{itemId}
// ============================================================

/**
 * Fetch all item history records for a user
 * Returns a map of itemId → record
 */
export const fetchItemHistory = async (userId) => {
    try {
        const historyRef = collection(db, 'users', userId, 'itemHistory');
        const snapshot = await getDocs(historyRef);
        const history = {};

        snapshot.forEach(docSnap => {
            history[docSnap.id] = docSnap.data();
        });

        return history;
    } catch (error) {
        console.error('Error fetching item history:', error);
        return {};
    }
};

/**
 * Update a single item's history record (upsert)
 */
export const updateItemHistory = async (userId, itemId, record) => {
    try {
        const itemRef = doc(db, 'users', userId, 'itemHistory', itemId);
        await setDoc(itemRef, {
            ...record,
            lastUpdated: serverTimestamp()
        }, { merge: true });
    } catch (error) {
        console.error('Error updating item history:', error);
        // Non-blocking: spaced repetition tracking is best-effort
    }
};

// ============================================================
// Daily Practice State — tracks free user's 20 questions/day
// ============================================================

const getTodayDateString = () => new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD in local time

export const fetchDailyPracticeState = async (userId) => {
    try {
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        const today = getTodayDateString();

        if (userDoc.exists() && userDoc.data().dailyPractice) {
            const dp = userDoc.data().dailyPractice;
            if (dp.date === today) {
                return { date: dp.date, quizUsed: dp.quizUsed || false };
            }
        }

        // New day or no prior state — reset
        return { date: today, quizUsed: false };
    } catch (error) {
        console.error('Error fetching daily practice state:', error);
        return { date: getTodayDateString(), quizUsed: false };
    }
};

export const updateDailyPracticeState = async (userId, dailyPracticeData) => {
    try {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
            dailyPractice: dailyPracticeData,
            lastUpdated: serverTimestamp()
        });
    } catch (error) {
        console.error('Error updating daily practice state:', error);
    }
};

/**
 * Batch update multiple item history records
 * Used when migrating or initializing history from response logs
 */
export const batchUpdateItemHistory = async (userId, records) => {
    try {
        const promises = Object.entries(records).map(([itemId, record]) => {
            const itemRef = doc(db, 'users', userId, 'itemHistory', itemId);
            return setDoc(itemRef, {
                ...record,
                lastUpdated: serverTimestamp()
            }, { merge: true });
        });
        await Promise.all(promises);
    } catch (error) {
        console.error('Error batch updating item history:', error);
    }
};

/**
 * Reset all user progress back to defaults.
 * Clears stats, domain mastery, sessions, and all item history subcollection docs.
 * Preserves account-level fields (experienceLevel, paid status, etc.)
 */
export const resetAllProgress = async (userId) => {
    try {
        const userRef = doc(db, 'users', userId);

        // 1. Reset the main user document stats
        await updateDoc(userRef, {
            totalQuestions: 0,
            correctAnswers: 0,
            totalTime: 0,
            lastSession: null,
            domainMastery: defaultDomainMastery,
            lastPracticedByDomain: {},
            recentSessions: [],
            weakestTopics: [],
            dailyPractice: null,
            lastUpdated: serverTimestamp()
        });

        // 2. Delete all itemHistory subcollection documents
        const historyRef = collection(db, 'users', userId, 'itemHistory');
        const snapshot = await getDocs(historyRef);
        const deletePromises = [];
        snapshot.forEach(docSnap => {
            deletePromises.push(deleteDoc(doc(db, 'users', userId, 'itemHistory', docSnap.id)));
        });
        await Promise.all(deletePromises);

        return true;
    } catch (error) {
        console.error('Error resetting progress:', error);
        throw error;
    }
};